import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiClient } from '../config/api';
import { toast } from 'react-hot-toast';

// Create the context with a default value
export const AuthContext = createContext({
  user: null,
  login: () => {},
  loginWithOTP: () => {},
  logout: () => {},
  loading: true,
  error: null
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const verifyToken = useCallback(async (retries = 3) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await apiClient.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      if (error.response?.status === 429 && retries > 0) {
        // Wait for 1 second before retrying
        await new Promise(resolve => setTimeout(resolve, 1000));
        return verifyToken(retries - 1);
      }
      
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        setUser(null);
      } else {
        console.error('Auth verification error:', error);
        toast.error('Authentication error. Please try logging in again.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  const login = async (credentials) => {
    try {
      const response = await apiClient.post('/auth/login', credentials);
      const { token, user: userData } = response.data;
      
      setUser(userData);
      localStorage.setItem('token', token);
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setError(null);
      return response.data;
    } catch (error) {
      setError(error.response?.data?.message || 'Login failed');
      throw error;
    }
  };

  const loginWithOTP = async (email, otp) => {
    try {
      const response = await apiClient.post('/auth/verify-otp', { email, otp });
      const { token, user: userData } = response.data;
      
      setUser(userData);
      localStorage.setItem('token', token);
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setError(null);
      return response.data;
    } catch (error) {
      setError(error.response?.data?.message || 'OTP verification failed');
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('token');
    delete apiClient.defaults.headers.common['Authorization'];
    setError(null);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      loginWithOTP, 
      logout, 
      loading, 
      error 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};